import React from 'react';

// Libraries
import chroma from 'chroma-js';
import { areEqual, FixedSizeList as List } from 'react-window';

// Components
import { GenericEmptyState } from '../../misc';
import CorrelationChartVirtualizedRow from './CorrelationChartVirtualizedRow';

// Types
import ComparisonHolding from '@/types/comparison-holding';
import CorrelationData from '@/types/correlation-data';

// Utils
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import { CORRELATION_MATRIX_COLOURS, getCorrelationLabelDisplayedText } from '@/utils/correlation-matrix';

type Props = {
  holdings: ComparisonHolding[];
  correlation?: CorrelationData[];
  onClick?: (event: React.MouseEvent) => void;
  onCorrelationPointHover: (message: string) => void;
};

class CorrelationChartVirtualized extends React.PureComponent<Props> {
  colorScale = chroma.scale(CORRELATION_MATRIX_COLOURS);

  renderTable = () => {
    const { holdings, correlation } = this.props;
    if (!correlation) {
      return null;
    }

    // Filter duplicates
    const uniqueCorrelation = correlation.filter(c => c.holdings[0] !== c.holdings[1]);
    const uniqueHoldings = Array.from(new Set(holdings.map(holding => holding.id))).map(id => holdings.find(holding => holding.id === id)!);

    const footerColumns = uniqueHoldings.map(holding => (
      <td key={holding.id} className="correlation-x-label">
        <span
          className="correlation-label"
          onMouseLeave={e => this.props.onCorrelationPointHover('')}
          onMouseEnter={e => this.props.onCorrelationPointHover(holding.name ? formatLocalizedString(holding.name) : '')}
        >
          {getCorrelationLabelDisplayedText(holding)}
        </span>
      </td>
    ));

    const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
      if (index === 0) {
        return null;
      }
      const rowHolding = uniqueHoldings[index];
      const rowData = uniqueHoldings.slice(0, index).map(compareHolding => {
        const correlationPoint = uniqueCorrelation.find(c => c.holdings.includes(rowHolding.id) && c.holdings.includes(compareHolding.id));
        return correlationPoint || { holdings: [rowHolding.id, compareHolding.id], value: null };
      });

      return (
        <div style={style}>
          <CorrelationChartVirtualizedRow
            key={rowHolding.id}
            onHover={this.props.onCorrelationPointHover}
            colorScale={this.colorScale}
            holdings={uniqueHoldings}
            rowHolding={rowHolding}
            data={rowData}
          />
        </div>
      );
    };

    return (
      <div className="correlation-chart-container">
        <List height={500} itemCount={uniqueHoldings.length} itemSize={32} width="100%">
          {React.memo(Row, areEqual)}
        </List>
        <table className="correlation-table">
          <tbody>
            <tr className="sticky-footer">
              <td key={0} />
              {footerColumns}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  renderContent = () => {
    const { holdings, correlation } = this.props;

    // No correlation data if less than 2 holdings - show proper message.
    if (holdings && holdings.length < 2) {
      return <GenericEmptyState body={formatMessage('Charts.Correlation.SingleHolding')} />;
    }

    // Show proper message if no correlation data for selected period.
    if (!correlation || !correlation.length || !holdings) {
      return <GenericEmptyState body={formatMessage('Charts.Correlation.NoData')} />;
    }

    return this.renderTable();
  };

  render() {
    const { onClick } = this.props;

    return (
      <div className="correlation-chart virtualized" onClick={onClick}>
        {this.renderContent()}
      </div>
    );
  }
}

export default CorrelationChartVirtualized;
